import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, SHIPPING_TIME, FREE_SHIPPING_THRESHOLD, DEFAULT_QUANTITY } from '../data/mock';
import { ShoppingCart, ChevronRight, Minus, Plus, Truck, Shield, RotateCcw, CheckCircle, Bolt, Zap, Package, Scale, Timer, Beaker, FileText } from 'lucide-react';
import { useCart } from '../context/CartContext';

// Placeholder Image Component
const PlaceholderImage = ({ className = '' }) => (
  <div className={`bg-neutral-800 flex items-center justify-center ${className}`}>
    <div className="text-center">
      <Package className="w-16 h-16 text-neutral-600 mx-auto mb-3" />
      <span className="text-neutral-500 text-sm">Product Image</span>
    </div>
  </div>
);

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [quantity, setQuantity] = useState(2);
  const { addToCart } = useCart();
  
  const product = products.find(p => p.slug === slug);
  const category = categories.find(c => c.id === product?.categoryId);
  const relatedProducts = products.filter(p => p.categoryId === product?.categoryId && p.id !== product?.id).slice(0, 3);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | Axon Muscle`;
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <Header />
        <div className="pt-[120px] flex items-center justify-center py-20">
          <div className="text-center">
            <h1 className="text-2xl font-display text-white uppercase tracking-wide mb-4">Product Not Found</h1>
            <Link to="/shop" className="text-axon-500 hover:text-axon-400 font-medium">
              ← Back to Shop
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Breadcrumb */}
        <div className="bg-neutral-900 border-b border-neutral-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-neutral-500 hover:text-axon-500 transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 text-neutral-600" />
              <Link to="/shop" className="text-neutral-500 hover:text-axon-500 transition-colors">Shop</Link>
              <ChevronRight className="w-4 h-4 text-neutral-600" />
              <Link to={`/shop?category=${category?.slug}`} className="text-neutral-500 hover:text-axon-500 transition-colors">
                {category?.name}
              </Link>
              <ChevronRight className="w-4 h-4 text-neutral-600" />
              <span className="text-white font-medium truncate">{product.name}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Product Image */}
            <div className="space-y-4">
              <div className="relative bg-neutral-900 rounded-3xl border border-neutral-800 overflow-hidden">
                <div className="aspect-square flex items-center justify-center p-6">
                  {product.image ? (
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <PlaceholderImage className="w-full h-full" />
                  )}
                </div>
                {product.badge && (
                  <span className="absolute top-6 left-6 px-4 py-2 bg-axon-600 text-white text-sm font-bold uppercase tracking-wider rounded-lg shadow-lg">
                    {product.badge}
                  </span>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-4">
                  <Bolt className="w-4 h-4" />
                  {category?.name}
                </div>
                <h1 className="font-display text-2xl lg:text-4xl text-white uppercase tracking-tight mb-2">
                  {product.name}
                </h1>
                <p className="text-neutral-500 text-sm">SKU: {product.sku}</p>
              </div>

              <div className="mb-6">
                <span className="text-5xl font-bold text-white">${product.price.toFixed(2)}</span>
                <span className="text-neutral-500 ml-2 text-sm">USD</span>
                <span className="ml-4 inline-flex items-center px-3 py-1 bg-green-500/20 text-green-400 text-sm font-semibold rounded-lg">
                  In Stock
                </span>
              </div>

              {/* Description Section */}
              <div className="mb-8">
                <h3 className="font-display font-bold text-white uppercase tracking-wide mb-4 text-lg">
                  Description
                </h3>
                <p className="text-neutral-400 leading-relaxed">
                  {product.description}
                </p>
              </div>

              {/* Product Specs Section */}
              {product.specs && (
                <div className="mb-8">
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Beaker className="w-5 h-5 text-axon-500" />
                    Product Specs
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {product.specs.netWeight && (
                      <div className="p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                        <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                          <Scale className="w-4 h-4" />
                          Net Weight
                        </div>
                        <p className="text-white font-semibold">{product.specs.netWeight}</p>
                      </div>
                    )}
                    {product.specs.quantity && (
                      <div className="p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                        <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                          <Package className="w-4 h-4" />
                          Quantity
                        </div>
                        <p className="text-white font-semibold">{product.specs.quantity}</p>
                      </div>
                    )}
                    {product.specs.servings && (
                      <div className="p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                        <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                          <Timer className="w-4 h-4" />
                          Servings
                        </div>
                        <p className="text-white font-semibold">{product.specs.servings}</p>
                      </div>
                    )}
                    {product.specs.includes && (
                      <div className="p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                        <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                          <Package className="w-4 h-4" />
                          Includes
                        </div>
                        <p className="text-white font-semibold">{product.specs.includes}</p>
                      </div>
                    )}
                    {product.specs.duration && (
                      <div className="p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                        <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                          <Timer className="w-4 h-4" />
                          Duration
                        </div>
                        <p className="text-white font-semibold">{product.specs.duration}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ingredients Label Section - Single Image */}
              {product.ingredientsImage && (
                <div className="mb-8">
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-axon-500" />
                    Supplement Facts & Ingredients
                  </h3>
                  <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
                    <img 
                      src={product.ingredientsImage}
                      alt={`${product.name} - Supplement Facts & Ingredients`}
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              )}

              {/* Ingredients Label Section - Multiple Images (for stacks/bundles) */}
              {product.ingredientsImages && product.ingredientsImages.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-axon-500" />
                    Supplement Facts & Ingredients
                  </h3>
                  <div className="space-y-4">
                    {product.ingredientsImages.map((item, index) => (
                      <div key={index} className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
                        <div className="bg-neutral-800 px-4 py-2 border-b border-neutral-700">
                          <span className="text-axon-400 font-semibold text-sm uppercase tracking-wider">{item.name}</span>
                        </div>
                        <img 
                          src={item.url}
                          alt={`${item.name} - Supplement Facts & Ingredients`}
                          className="w-full h-auto"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Benefits Section */}
              {product.keyBenefits && product.keyBenefits.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-axon-500" />
                    Key Benefits
                  </h3>
                  <div className="space-y-3">
                    {product.keyBenefits.map((benefit, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                        <CheckCircle className="w-5 h-5 text-axon-500 flex-shrink-0 mt-0.5" />
                        <span className="text-neutral-300">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Usage Section */}
              {product.usage && (
                <div className="mb-8">
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-electric-400" />
                    How To Use
                  </h3>
                  <div className="p-4 bg-gradient-to-r from-axon-600/10 to-electric-500/10 border border-axon-500/30 rounded-xl">
                    <p className="text-neutral-300">{product.usage}</p>
                  </div>
                </div>
              )}

              {/* Features Tags */}
              <div className="mb-8">
                <div className="flex flex-wrap gap-2">
                  {product.features.map((feature, i) => (
                    <span key={i} className="px-3 py-1.5 bg-neutral-800 border border-neutral-700 rounded-lg text-neutral-400 text-sm">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center bg-neutral-800 rounded-xl overflow-hidden border border-neutral-700">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-4 text-neutral-400 hover:text-white hover:bg-neutral-700 transition-colors"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="w-14 text-center font-semibold text-white text-lg">{quantity}</span>
                    <button 
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-4 text-neutral-400 hover:text-white hover:bg-neutral-700 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button hover:shadow-glow-cyan"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Add to Cart
                  </button>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: Truck, text: `Free shipping $${FREE_SHIPPING_THRESHOLD}+` },
                  { icon: Shield, text: 'Lab Tested' },
                  { icon: RotateCcw, text: '30-Day Returns' },
                ].map((badge, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                    <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center mb-2">
                      <badge.icon className="w-5 h-5 text-axon-500" />
                    </div>
                    <span className="text-neutral-400 text-xs">{badge.text}</span>
                  </div>
                ))}
              </div>

              {/* Shipping Info */}
              <div className="mt-6 flex items-center gap-3 p-4 bg-neutral-900 border border-neutral-800 rounded-xl">
                <Package className="w-5 h-5 text-electric-400" />
                <span className="text-neutral-400 text-sm">
                  Estimated delivery: <span className="text-white font-medium">{SHIPPING_TIME}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-display text-2xl lg:text-3xl text-white uppercase tracking-tight">
                  Related Products
                </h2>
                <Link 
                  to={`/shop?category=${category?.slug}`}
                  className="text-axon-500 hover:text-axon-400 font-semibold text-sm flex items-center gap-1"
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedProducts.map((relatedProduct) => (
                  <Link 
                    key={relatedProduct.id}
                    to={`/product/${relatedProduct.slug}`}
                    className="group bg-neutral-900 rounded-2xl border border-neutral-800 hover:border-axon-500/50 overflow-hidden transition-all hover:shadow-card-hover"
                  >
                    <div className="aspect-square bg-neutral-800 relative overflow-hidden">
                      {relatedProduct.image ? (
                        <img 
                          src={relatedProduct.image}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <PlaceholderImage className="w-full h-full" />
                      )}
                      {relatedProduct.badge && (
                        <span className="absolute top-3 left-3 px-3 py-1 bg-axon-600 text-white text-xs font-bold uppercase tracking-wider rounded-lg">
                          {relatedProduct.badge}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-white mb-2 group-hover:text-axon-400 transition-colors line-clamp-2">
                        {relatedProduct.name}
                      </h3>
                      <span className="text-xl font-bold text-white">
                        ${relatedProduct.price.toFixed(2)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
